import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ActionButton from '@applied/dragonfruit/src/components/ActionButton/ActionButton';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import { useSnackbar } from '@applied/dragonfruit/src/components/PageSnackbar/snackbar_provider';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import { Timestamp } from '@applied/proto/google/protobuf/timestamp_pb';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateQueryKeys, optimisticFieldUpdate, showAndLogError } from 'api/request';

import { ConfirmationModal } from 'components/WebOfferPage/UserActionSection/ConfirmationModal/ConfirmationModal';
import { requestOfferLetter } from 'components/WebOfferPage/UserActionSection/user_action_section_requests';
import { useWebOffer } from 'components/WebOfferPage/web_offer_context';

import './user-action-section.scss';

function formatDeadline(deadline: Timestamp): string {
  const deadlineDate = Timestamp.toDate(deadline);
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
  return formatter.format(deadlineDate);
}

type UserActionSectionProps = {
  viewOnly?: boolean;
};

export function UserActionSection(props: UserActionSectionProps): JSX.Element {
  const { viewOnly } = props;
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const {
    deadline,
    selection: { isFinal, setIsFinal },
    isRequestingOfferLetter,
    setIsRequestingOfferLetter,
  } = useWebOffer();
  const history = useHistory();
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();

  const handleRequestOfferLetter = useCallback(async (): Promise<void> => {
    if (isRequestingOfferLetter) {
      return;
    }

    setIsRequestingOfferLetter(true);
    const originalIsFinal = isFinal;

    try {
      await optimisticFieldUpdate(
        originalIsFinal,
        true,
        (updatedIsFinal) => {
          setIsFinal(updatedIsFinal);
        },
        async () => {
          const response = await requestOfferLetter(history);
          if (!response) {
            throw new Error('Failed to request offer letter');
          }
        },
        async () => {
          await invalidateQueryKeys(queryClient);
        },
        (error: Error) => {
          showAndLogError(snackbar, 'Failed to request offer letter', error);
        },
      );
    } finally {
      setIsRequestingOfferLetter(false);
    }
  }, [
    history,
    snackbar,
    isFinal,
    setIsFinal,
    queryClient,
    isRequestingOfferLetter,
    setIsRequestingOfferLetter,
  ]);

  return (
    <Flex
      className="user-action-section-container"
      direction="column"
      gap="lg"
      alignment="center"
      justify="center"
      padding="xxl"
    >
      <div id="ready-to-sign">
        <Flex
          className="user-action-section"
          direction="column"
          gap="md"
          padding="lg"
          alignment="center"
          justify="center"
        >
          <Typography variant="header">Ready to sign?</Typography>
          {isFinal ? (
            <Typography variant="body">Your offer is being prepared!</Typography>
          ) : (
            <ActionButton
              onClick={() => setIsConfirmationModalOpen(true)}
              appearance="primary"
              loading={isRequestingOfferLetter}
            >
              <Typography variant="body">Request my offer letter</Typography>
            </ActionButton>
          )}

          {deadline && (
            <Typography variant="caption">Offer deadline: {formatDeadline(deadline)}</Typography>
          )}
        </Flex>
      </div>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          onClose={() => setIsConfirmationModalOpen(false)}
          onSubmit={handleRequestOfferLetter}
          viewOnly={viewOnly}
        />
      )}
    </Flex>
  );
}
